import { setBasicInfo } from '../../../core/store/slices/basicInfoSlice';
import { setPersonalDetails } from '../../../core/store/slices/personalDetailsSlice';
import { EnrollmentFormData } from '../types/enrollment';
import { Dispatch } from 'redux';
import { StepConfig } from './stepsConfig';
import { setAvailability } from '../../../core/store/slices/availabilitySlice';
import { setEmergencyContact } from '../../../core/store/slices/emergencyContactSlice';
import { setPreferenceInformation } from '../../../core/store/slices/preferenceInformationSlice';

const DEFAULT_COACH_GENDER = '5';
const DEFAULT_COACH_ETHNICITY = '6';
const DEFAULT_COACH_ORIENTATION = '5';

export const stepConfig: Record<number, StepConfig> = {
  0: {
    key: 'ENROLLMENT_CONTACT_INFO',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch, userId: string): void => {
      dispatch(
        setBasicInfo({
          _id: userId,
          phone: data.phoneNumber ? `+1${data.phoneNumber}` : '',
          partnerId: data.partnerId,
          memberSource: data.memberSource,
        })
      );
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      phoneNumber: data.phoneNumber,
      partnerId: data.partnerId,
      memberSource: data.memberSource,
      stepKey: 'ENROLLMENT_CONTACT_INFO',
    }),
  },
  1: {
    key: 'ENROLLMENT_PERSONAL_DETAILS',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch): void => {
      dispatch(
        setPersonalDetails({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          preferredName: data.preferredName || '',
          email: data.email || '',
          dob: data.dob || '',
          races: data.races || [],
          sexualOrientation: data.sexualOrientation || '',
          // preferredLanguage: data.preferredLanguage || '',
          pronounsList: data.pronounsList || [],
          gender: data.gender || '',
          school: data.school || '',
          address: data.address || '',
          state: data.state || '',
          city: data.city || '',
          zip: data.zip || '',
        })
      );
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      firstName: data.firstName,
      lastName: data.lastName,
      preferredName: data.preferredName,
      email: data.email,
      dob: data.dob,
      races: data.races,
      sexualOrientation: data.sexualOrientation,
      pronounsList: data.pronounsList,
      gender: data.gender,
      school: data.school,
      address: data.address,
      state: data.state,
      city: data.city,
      zip: data.zip,
      isStudent: data.isStudent,
      preferredLanguage: '',
      stepKey: 'ENROLLMENT_PERSONAL_DETAILS',
    }),
  },
  2: {
    key: 'COMPLETE_ENROLLMENT',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch): void => {
      // No-op function - explicitly does nothing
      return;
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      stepKey: 'COMPLETE_ENROLLMENT',
    }),
  },
  3: {
    key: 'ENROLLMENT_PREFERENCE_INFORMATION',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch): void => {
      dispatch(
        setPreferenceInformation({
          hopeToGain: data.hopeToGain || '',
          preferredPartnerId: data.preferredPartnerId || undefined,
          preferredPartnerName: data.preferredPartnerName || undefined,
          preferredCoachGenders: data.preferredCoachGenders || DEFAULT_COACH_GENDER,
          preferredCoachEthnicities: data.preferredCoachEthnicities || [DEFAULT_COACH_ETHNICITY],
          preferredCoachSexualOrientations: data.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
        })
      );
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      hopeToGain: data.hopeToGain,
      preferredPartnerId: data.preferredPartnerId || undefined,
      preferredPartnerName: data.preferredPartnerName || undefined,
      preferredCoachGenders: data.preferredCoachGenders || DEFAULT_COACH_GENDER,
      preferredCoachEthnicities: data.preferredCoachEthnicities || [DEFAULT_COACH_ETHNICITY],
      preferredCoachSexualOrientations: data.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
      stepKey: 'ENROLLMENT_PREFERENCE_INFORMATION',
    }),
  },
  4: {
    key: 'ENROLLMENT_EMERGENCY_CONTACT',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch): void => {
      dispatch(
        setEmergencyContact({
          contactName: data.contactName || '',
          contactPhone: data.contactPhone || '',
          contactRelationship: data.contactRelationship || '',
        })
      );
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      contactName: data.contactName,
      contactPhone: data.contactPhone,
      contactRelationship: data.contactRelationship,
      stepKey: 'ENROLLMENT_EMERGENCY_CONTACT',
    }),
  },
  
  5: {
    key: 'ENROLLMENT_AVAILABILITY',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch): void => {
      dispatch(
        setAvailability({
          shiftDays: data.shiftDays || [],
          shiftTime: data.shiftTime || '',
        })
      );
      dispatch(
        setPreferenceInformation({
          hopeToGain: data.hopeToGain || '',
          preferredPartnerId: data.preferredPartnerId || undefined,
          preferredPartnerName: data.preferredPartnerName || undefined,
          preferredCoachGenders: data.preferredCoachGenders || DEFAULT_COACH_GENDER,
          preferredCoachEthnicities: data.preferredCoachEthnicities || [DEFAULT_COACH_ETHNICITY],
          preferredCoachSexualOrientations: data.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
        })
      );
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      shiftTime: data.shiftTime,
      shiftDays: data.shiftDays,
      preferredCoachGenders: data.preferredCoachGenders || DEFAULT_COACH_GENDER,
      preferredCoachEthnicities: data.preferredCoachEthnicities || [DEFAULT_COACH_ETHNICITY],
      preferredCoachSexualOrientations: data.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
      stepKey: 'ENROLLMENT_AVAILABILITY',
    }),
  },
};
const toEthnicityArray = (val: string | string[] | undefined): string[] => {
  if (Array.isArray(val)) return val.length ? val : [DEFAULT_COACH_ETHNICITY];
  return val ? [val] : [DEFAULT_COACH_ETHNICITY];
};

export const stepConfig2: Record<number, StepConfig> = {
  0: {
    key: 'ENROLLMENT_PREFERENCE_INFORMATION',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch): void => {
      dispatch(
        setPreferenceInformation({
          hopeToGain: data.hopeToGain || '',
          preferredPartnerId: data.preferredPartnerId || undefined,
          preferredPartnerName: data.preferredPartnerName || undefined,
          preferredCoachGenders: data.preferredCoachGenders || DEFAULT_COACH_GENDER,
          preferredCoachEthnicities: toEthnicityArray(data.preferredCoachEthnicities),
          preferredCoachSexualOrientations: data.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
        })
      );
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      hopeToGain: data.hopeToGain,
      preferredPartnerId: data.preferredPartnerId || undefined,
      preferredPartnerName: data.preferredPartnerName || undefined,
      preferredCoachGenders: data.preferredCoachGenders || DEFAULT_COACH_GENDER,
      preferredCoachEthnicities: toEthnicityArray(data.preferredCoachEthnicities),
      preferredCoachSexualOrientations: data.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
      stepKey: 'ENROLLMENT_PREFERENCE_INFORMATION',
    }),
  },
  1: {
    key: 'ENROLLMENT_AVAILABILITY',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch): void => {
      dispatch(
        setAvailability({
          shiftDays: data.shiftDays || [],
          shiftTime: data.shiftTime || '',
        })
      );
      dispatch(
        setPreferenceInformation({
          hopeToGain: data.hopeToGain || '',
          preferredPartnerId: data.preferredPartnerId || undefined,
          preferredPartnerName: data.preferredPartnerName || undefined,
          preferredCoachGenders: data.preferredCoachGenders || DEFAULT_COACH_GENDER,
          preferredCoachEthnicities: toEthnicityArray(data.preferredCoachEthnicities),
          preferredCoachSexualOrientations: data.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
        })
      );
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      shiftTime: data.shiftTime,
      shiftDays: data.shiftDays,
      preferredCoachGenders: data.preferredCoachGenders || DEFAULT_COACH_GENDER,
      preferredCoachEthnicities: toEthnicityArray(data.preferredCoachEthnicities),
      preferredCoachSexualOrientations: data.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
      stepKey: 'ENROLLMENT_AVAILABILITY',
    }),
  },
  2: {
    key: 'ENROLLMENT_EMERGENCY_CONTACT',
    updateRedux: (data: EnrollmentFormData, dispatch: Dispatch): void => {
      dispatch(
        setEmergencyContact({
          contactName: data.contactName || '',
          contactPhone: data.contactPhone || '',
          contactRelationship: data.contactRelationship || '',
        })
      );
    },
    prepareApiData: (data: EnrollmentFormData, userId: string) => ({
      memberId: userId,
      contactName: data.contactName,
      contactPhone: data.contactPhone,
      contactRelationship: data.contactRelationship,
      stepKey: 'ENROLLMENT_EMERGENCY_CONTACT',
    }),
  },
 
};